import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ReactCropperElement } from "react-cropper";
import { mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import ChunkedUploader from "common/ChunkUploader";
import Button from "components/Button";
import DropzoneFile from "components/DropzoneFile";
import { useDocumentSourcePicker } from "components/DropzoneFile/useDocumentSourcePicker";
import { FileUploader } from "components/FileUploader";
import Modal from "components/Modal";
import MultiSelect from "components/MultiSelect";
import Select from "components/Select";
import UploadDocumentsButton from "components/UploadDocumentsButton";
import { UPLOAD_CATEGORY } from "constants/base";
import { VISIBILITY_LIST } from "constants/haulpay-broker";
import { FileUploadProvider, FileProgress, useUpload } from "context/FileUpload";
import { formatFileSize } from "helpers/formatFileSize";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { IOption } from "models/Form";
import { IUpdatedFRAttachment } from "models/HaulPay";
import type { FileProps } from "models/Base";
import { deleteFRAttachmentAPI } from "services/api/haulpay";
import { CropModal } from "features/main/HaulPay/Shared/EditFundingRequestV2/Manage/components/CropModal";
import { CROPPED_CANVAS_OPTIONS } from "features/main/HaulPay/Shared/EditFundingRequestV2/Manage/components/imageEditor/cropperUtils";
import { resolveAttachmentMimeType } from "./fileTypeUtils";
import styles from "./style.module.scss";

import "cropperjs/dist/cropper.css";

type PureUploadFileProps = {
  label?: string;
  prefixNode?: React.ReactNode;
  className?: string;
  categories?: IOption[];
  defaultCategory?: IOption;
  disableCategorySelection?: boolean;
  multiple?: boolean;
  visibility?: boolean;
  urlCallback: string;
  onUploadFinished: (files: any, fileId: string) => void;
  autoUpload?: boolean;
  customerId?: string;
  getPreviewUrl?: (url: string) => void;
  updatedFile?: IUpdatedFRAttachment[];
  setFile?: (file: IUpdatedFRAttachment[]) => void;
  maxSizeBytes?: number;
  triggerVariant?: "dropzone" | "button";
  modalZIndex?: string;
  onUploadFailed?: () => void;
};

const DEFAULT_MAX_SIZE_BYTES = 25 * 1024 * 1024;
const getFileSizeLimitError = (maxSizeBytes: number) =>
  `This file exceeds the ${Math.round(
    maxSizeBytes / (1024 * 1024)
  )}MB limit. Please reduce the file size and try again.`;
type ActiveUploadModal = "upload" | "image" | null;
type InitialFileDetails = {
  categories?: string;
  visibleTo?: string[];
};

/* ------------------------------------------------------------------ */
/*                            File row                               */
/* ------------------------------------------------------------------ */

function PendingFile({
  file,
  onRemoveFile
}: Readonly<{ file: any; onRemoveFile: (id: string) => void }>) {
  return (
    <div className={styles.pureFile}>
      <span>
        {file.name} {formatFileSize(file.size)}
      </span>

      <div className={styles.progressWrapper}>
        <div className={styles.progress}>
          <div
            className={clsx(styles.currentProgress, {
              "!bg-success": file.progress >= 100 || file.status === "success",
              "bg-error": file.status === "error"
            })}
            style={{ width: `${file.progress}%` }}
          />
        </div>
        <button type="button" onClick={() => onRemoveFile(file.id)}>
          <Icon path={mdiTrashCanOutline} className={styles.closeIcon} />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*                             Uploader                               */
/* ------------------------------------------------------------------ */

function PureUploadFile({
  label,
  prefixNode,
  className,
  categories,
  defaultCategory,
  disableCategorySelection = false,
  multiple = false,
  visibility = false,
  urlCallback,
  onUploadFinished,
  autoUpload = false,
  customerId = undefined,
  getPreviewUrl,
  updatedFile = [],
  setFile,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  triggerVariant = "dropzone",
  modalZIndex,
  onUploadFailed
}: Readonly<PureUploadFileProps>) {
  const categoryOptions = categories || UPLOAD_CATEGORY;
  const [activeModal, setActiveModal] = useState<ActiveUploadModal>(null);
  const showUploadModal = activeModal === "upload";
  const showImageEditor = activeModal === "image";
  const [documentType, setDocumentType] = useState<Array<IOption>>(
    defaultCategory ? [defaultCategory] : []
  );
  const [visibleTo, setVisibleTo] = useState<string[]>([]);
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [sizeError, setSizeError] = useState("");
  const [originalFiles, setOriginalFiles] = useState<Map<string, File>>(new Map());
  const cropperRef = useRef<ReactCropperElement>(null);

  const {
    files,
    addFile,
    setFileProgress,
    setFileStatus,
    updateFileCategory,
    updateFileVisibility,
    removeFile,
    emptyFiles,
    replaceFile
  } = useUpload();

  const currentImage = useMemo(
    () => files.find((file) => file.type?.startsWith("image/")),
    [files]
  );
  const updatedAttachment = updatedFile[0];

  const initializeFileDetails = (file: FileProgress, details: InitialFileDetails) => {
    if (details.categories) {
      file.setCategories(details.categories);
      try {
        (file.file as FileProps).categories = details.categories;
      } catch {
        // The upload state update below handles immutable File implementations.
      }
      updateFileCategory(file.id, details.categories);
    }

    if (details.visibleTo) {
      file.setVisibleTo(details.visibleTo);
      try {
        (file.file as FileProps).visible_to = details.visibleTo;
      } catch {
        // The upload state update below handles immutable File implementations.
      }
      updateFileVisibility(file.id, details.visibleTo);
    }
  };

  const currentCategoryOptions = useMemo(() => {
    if (!currentImage?.categories) return documentType;
    const values = new Set(
      (Array.isArray(currentImage.categories)
        ? currentImage.categories
        : currentImage.categories.split(",")
      )
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean)
    );
    return categoryOptions.filter((option) => {
      const value = String(option.value || "")
        .trim()
        .toLowerCase();
      const label = String(option.label || "")
        .trim()
        .toLowerCase();
      return values.has(value) || values.has(label);
    });
  }, [categoryOptions, currentImage?.categories, documentType]);

  const categoriesHandler = (cats: string[] | string, id: string) => {
    const categoryValues = Array.isArray(cats) ? cats : [cats];
    const value = multiple ? categoryValues.join(",") : categoryValues[0] || "";
    updateFileCategory(id, value);
  };

  const visibleToHandler = (list: string[], id: string) => {
    updateFileVisibility(id, list);
  };

  const handleSingleUpload = async (fp: any /* FileProgress */) => {
    const id = fp.id;

    if (customerId === "") {
      onUploadFinished([fp], id);
      setActiveModal(null);
      return;
    }

    setFileStatus(id, "uploading");

    const fileToSend: File = fp.file ?? fp;

    const uploader = new ChunkedUploader({
      url: "/upload/chunk/",
      urlCallback: customerId ? `${urlCallback}/${customerId}/file/${id}` : `${urlCallback}/${id}`,
      file: fileToSend,
      onProgress: (progress) => setFileProgress(id, progress)
    });

    try {
      await uploader.upload();
      setFileStatus(id, "success");
      showSuccessMessage("File uploaded successfully.");

      const original = originalFiles.get(id);
      const uploaded = files.find((f) => f.id === id) || fp;

      const completeFile = {
        ...uploaded,
        name: uploaded.name || original?.name || fileToSend.name,
        preview_url: uploader.previewUrl,
        type: uploaded.type || original?.type || fileToSend.type,
        size: uploaded.size || original?.size || fileToSend.size,
        lastModified: uploaded.lastModified || original?.lastModified || fileToSend.lastModified
      };

      onUploadFinished([completeFile] as any[], id);
      getPreviewUrl && getPreviewUrl(uploader.previewUrl);
      setActiveModal(null);

      if (fp) {
        setFile?.([]);
        emptyFiles();
      }
    } catch {
      setFileStatus(id, "error");
      showErrorMessage("Upload failed");
      onUploadFailed?.();
    }
  };

  const handleUpload = async (list: any[]) => {
    if (!list?.length) {
      showErrorMessage("No files to upload.");
      return;
    }
    for (const fp of list) {
      if (fp) await handleSingleUpload(fp);
    }
  };

  const getValidFiles = (incomingFiles: File[]) => {
    const validFiles = incomingFiles.filter((file) => file.size <= maxSizeBytes);

    if (validFiles.length < incomingFiles.length) {
      setSizeError(getFileSizeLimitError(maxSizeBytes));
    } else {
      setSizeError("");
    }

    return validFiles;
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (!acceptedFiles?.length) {
      showErrorMessage("No files dropped.");
      return;
    }

    const validFiles = getValidFiles(acceptedFiles);
    if (!validFiles.length) {
      return;
    }

    const firstIsImage = validFiles[0].type.includes("image");
    setIsImageUploaded(firstIsImage);

    const initialCategories = documentType.map((type) => type.label).join(",");
    const newFiles = validFiles.map((raw) => {
      const fp = addFile(raw);
      setOriginalFiles((prev) => new Map(prev).set(fp.id, raw));
      initializeFileDetails(fp, { categories: initialCategories });
      return fp;
    });

    if (firstIsImage) setActiveModal("image");
    if (!firstIsImage && !autoUpload) setActiveModal("upload");

    if (autoUpload && newFiles.length > 0 && !firstIsImage) {
      await handleUpload(newFiles);
    }
  };

  const processSelectedFile = async (
    selected: File,
    initialDetails?: InitialFileDetails
  ): Promise<string | undefined> => {
    if (selected.size > maxSizeBytes) {
      setSizeError(getFileSizeLimitError(maxSizeBytes));
      return undefined;
    }

    setSizeError("");

    const isImg = selected.type.includes("image");
    setIsImageUploaded(isImg);

    const fp = addFile(selected);
    setOriginalFiles((prev) => new Map(prev).set(fp.id, selected));

    const initialCategories =
      initialDetails?.categories || documentType.map((type) => type.label).join(",");
    initializeFileDetails(fp, {
      categories: initialCategories,
      visibleTo: initialDetails?.visibleTo
    });

    if (autoUpload && !isImg) {
      await handleSingleUpload(fp);
    }

    if (isImg) setActiveModal("image");
    if (!isImg && !autoUpload) setActiveModal("upload");

    return fp.id;
  };

  const onFileInputChange = async (event: any) => {
    const selectedFiles = Array.from<File>(event.target.files ?? []);
    if (!selectedFiles.length) {
      showErrorMessage("No file selected.");
      return;
    }

    if (multiple) {
      await onDrop(selectedFiles);
    } else {
      await processSelectedFile(selectedFiles[0]);
    }

    event.target.value = ""; // allow reselect
  };

  const { fileInputRef: directFileInputRef, openPicker } =
    useDocumentSourcePicker(onFileInputChange);

  const handleUploadClick = async () => {
    try {
      if (files.length === 0) {
        showErrorMessage("No files to upload.");
        return;
      }

      const queue = files.filter((f) => f.status === "onProcess");
      if (!queue.length) {
        showErrorMessage("No files available for upload.");
        return;
      }

      const updatedCategory = updatedFile[0]?.category;
      const fallbackCategory = Array.isArray(updatedCategory)
        ? updatedCategory.join(",")
        : updatedCategory || "";
      const selectedCategory = documentType.map((option) => option.label).join(",");
      if (
        !disableCategorySelection &&
        queue.some((file) => !file.categories && !fallbackCategory && !selectedCategory)
      ) {
        showErrorMessage("Please select a category before uploading.");
        return;
      }

      if (
        visibility &&
        queue.some((file) => !file.visible_to?.length && !updatedFile[0]?.visibleTo?.length)
      ) {
        showErrorMessage("Please select who can view this document.");
        return;
      }

      if (updatedFile.length > 0) {
        const { frId, id: attachmentId } = updatedFile[0];
        if (!frId || !attachmentId) {
          showErrorMessage("Invalid attachment data.");
          return;
        }
        await deleteFRAttachmentAPI(frId, attachmentId);
      }

      queue.forEach((f) => {
        if (!autoUpload && !f.categories && updatedFile.length > 0) {
          categoriesHandler(updatedFile[0].category, f.id);
        }
        if (visibility && !f.visible_to?.length && updatedFile.length > 0) {
          visibleToHandler(updatedFile[0].visibleTo, f.id);
        }
      });

      await handleUpload(queue);
    } catch {
      showErrorMessage("An error occurred during upload.");
    }
  };

  const handleOpenUploadModal = async () => openPicker();

  const handleChangeDocumentType = (value: IOption[]) => {
    setDocumentType(value);
    files.forEach((f) =>
      categoriesHandler(
        value.map((i) => i.label),
        f.id
      )
    );
  };

  const handleCrop = async () => {
    if (!currentImage || !cropperRef.current?.cropper) {
      showErrorMessage("No image to crop.");
      return false;
    }

    const canvas = cropperRef.current.cropper.getCroppedCanvas(CROPPED_CANVAS_OPTIONS);
    if (!canvas) {
      showErrorMessage("Failed to crop image.");
      return false;
    }

    const outputType = currentImage.type === "image/png" ? "image/png" : "image/jpeg";
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, outputType, outputType === "image/jpeg" ? 0.9 : undefined)
    );
    if (!blob) {
      showErrorMessage("Failed to crop image.");
      return false;
    }

    const extension = outputType === "image/png" ? "png" : "jpg";
    const baseName = currentImage.name.replace(/\.[^.]+$/, "") || "image";
    replaceFile(
      currentImage.id,
      new File([blob], `${baseName}.${extension}`, {
        type: outputType,
        lastModified: Date.now()
      })
    );
    return true;
  };

  const onRemoveFile = (id: string) => {
    removeFile(id);
    setOriginalFiles((prev) => {
      const map = new Map(prev);
      map.delete(id);
      return map;
    });
    setIsImageUploaded(false);
    setActiveModal(null);
  };

  const onFileFromUrl = async (
    url: string,
    updated: IUpdatedFRAttachment,
    signal?: AbortSignal
  ) => {
    try {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error("Failed to fetch file");
      const blob = await response.blob();
      const f = new File([blob], updated.filename || "file_from_url.jpg", {
        type: resolveAttachmentMimeType(updated, blob.type)
      });
      const updatedCats =
        typeof updated.category === "string"
          ? updated.category.split(",").map((category) => category.trim())
          : updated.category ?? [];
      const normalizedCategories = updatedCats.filter(Boolean);
      const normalizedVisibility = updated.visibleTo?.length
        ? updated.visibleTo
        : updated.visible_to || [];
      const fileId = await processSelectedFile(f, {
        categories: normalizedCategories.join(","),
        visibleTo: normalizedVisibility
      });
      if (!fileId) return;

      setDocumentType(
        categoryOptions.filter(
          (item) =>
            normalizedCategories.includes(item.label) ||
            normalizedCategories.includes(String(item.value))
        )
      );
      setVisibleTo(normalizedVisibility);
    } catch (error) {
      if ((error as Error)?.name === "AbortError") return;
      showErrorMessage("Failed to load attachment for editing.");
      setFile?.([]);
      setActiveModal(null);
    }
  };

  // DnD handlers
  const handleDrag = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };
  const handleDropButton = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      (async () => {
        await onDrop(Array.from(droppedFiles));
        setDragActive(false);
      })();
    }
  };

  // Effects for updated file and cleanup between opens
  useEffect(() => {
    const controller = new AbortController();
    if (updatedAttachment) {
      void onFileFromUrl(updatedAttachment.downloadUrl, updatedAttachment, controller.signal);
    }
    return () => controller.abort();
  }, [updatedAttachment?.id]);

  const hasQueuedUploads = files.some((f) => f.status === "onProcess" || f.status === "uploading");

  useEffect(() => {
    if (activeModal === null && customerId !== "" && !hasQueuedUploads) {
      setDocumentType(defaultCategory ? [defaultCategory] : []);
      setVisibleTo([]);
      emptyFiles();
      setOriginalFiles(new Map());
      setSizeError("");
    }
  }, [activeModal, customerId, emptyFiles, defaultCategory, hasQueuedUploads]);

  useEffect(() => {
    if (customerId) {
      handleUploadClick();
    }
  }, [customerId]); // eslint-disable-line

  const resetUploadFlow = () => {
    setFile?.([]);
    emptyFiles();
    setActiveModal(null);
    setDocumentType(defaultCategory ? [defaultCategory] : []);
    setVisibleTo([]);
    setIsImageUploaded(false);
    setOriginalFiles(new Map());
    setSizeError("");
  };

  return (
    <div>
      {triggerVariant === "dropzone" ? (
        <FileUploader
          isDragActive={dragActive}
          isGlobalDisabled={false}
          loading={false}
          isUploading={files.some((file) => file.status === "uploading")}
          onDrag={handleDrag}
          onDrop={handleDropButton}
          onClick={handleOpenUploadModal}
          inputRef={directFileInputRef}
          onChange={onFileInputChange}
          className={className}
          multiple={multiple}
          maxSizeBytes={maxSizeBytes}
        />
      ) : (
        <div
          onDragOver={handleDrag}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDropButton}
          className={`${
            dragActive ? "bg-gray-800 border-dashed border-gray-700 border-4" : ""
          } p-4 rounded-lg transition-all border-gray-700`}>
          <UploadDocumentsButton
            label={label}
            prefixNode={prefixNode}
            className={className}
            onClick={handleOpenUploadModal}
          />
          <input
            ref={directFileInputRef}
            type="file"
            hidden
            accept="image/*,application/pdf,.pdf"
            multiple={multiple}
            onChange={onFileInputChange}
          />
        </div>
      )}
      {sizeError && !showUploadModal ? (
        <p className="mt-2 text-sm text-error">{sizeError}</p>
      ) : null}

      <Modal
        isOpen={showUploadModal}
        closeModal={resetUploadFlow}
        zIndex={modalZIndex}
        wrapperClass="!my-auto !py-4"
        dialogClass="!bg-background !p-0 !pt-2 !border-none !overflow-y-auto !min-h-72 justify-between doc-upload-modal"
        closeClass="!top-7 !right-6 !bg-transparent !text-placeholder"
        showCloseButton>
        <div className={styles.modalWrapper}>
          {files.length ? (
            files.map((fp) => (
              <PendingFile
                key={fp.id || fp.name || "pending-file"}
                file={fp}
                onRemoveFile={onRemoveFile}
              />
            ))
          ) : (
            <>
              <DropzoneFile onDrop={onDrop} onChange={onFileInputChange} />
              {sizeError && showUploadModal ? (
                <p className="text-sm text-error">{sizeError}</p>
              ) : null}
            </>
          )}

          {!autoUpload && !disableCategorySelection ? (
            <>
              {multiple ? (
                <MultiSelect
                  value={
                    documentType.length > 0
                      ? documentType
                      : updatedFile[0]?.category
                        ? categoryOptions.filter((opt) =>
                            updatedFile[0]!.category!.includes(opt.label)
                          )
                        : []
                  }
                  onChange={handleChangeDocumentType}
                  options={categoryOptions}
                  placeholder="Select Document Type"
                  label="&nbsp;&nbsp;Document"
                  className={`flex flex-col gap-2 ${
                    files.length > 0 && !isImageUploaded ? "pdf" : ""
                  }`}
                  direction="up"
                  disabled={!files.length}
                />
              ) : (
                <Select
                  value={documentType[0]}
                  options={categoryOptions}
                  direction="up"
                  placeholder="Select Document Type"
                  label="&nbsp;&nbsp;Document"
                  className="flex flex-col gap-2"
                  btnClass="!py-2 h-11 !px-4 !text-sm"
                  arrowClass="!right-2 !text-text"
                  onChange={(value) => handleChangeDocumentType([value])}
                  disabled={!files.length}
                />
              )}
            </>
          ) : null}

          {visibility ? (
            <Select
              value={VISIBILITY_LIST.find((item) => item.value === visibleTo.join(","))}
              options={VISIBILITY_LIST}
              direction="up"
              placeholder="Visible To"
              label="&nbsp;&nbsp;Visible To"
              className="flex flex-col gap-2"
              btnClass="!py-2 h-11 !px-4 !text-sm"
              arrowClass="!right-2 !text-text"
              onChange={(value) => {
                const list = value.value.split(",");
                setVisibleTo(list);
                files.forEach((f) => updateFileVisibility(f.id, list));
              }}
              disabled={!files.length}
            />
          ) : null}

          {!autoUpload || isImageUploaded || disableCategorySelection ? (
            <Button
              type="button"
              rounded
              className={styles.uploadBtn}
              disabled={
                disableCategorySelection
                  ? !files.length
                  : !updatedFile?.[0]?.category?.length &&
                    (!files.length ||
                      !documentType.length ||
                      (visibility ? !visibleTo.length : false)) &&
                    (!isImageUploaded ||
                      !documentType.length ||
                      (visibility ? !visibleTo.length : false)) &&
                    (!autoUpload || !isImageUploaded || !files.length)
              }
              onClick={handleUploadClick}>
              Upload
            </Button>
          ) : null}
        </div>
      </Modal>

      {currentImage && (
        <CropModal
          isOpen={showImageEditor}
          onClose={resetUploadFlow}
          file={{
            id: currentImage.id,
            name: currentImage.name,
            preview: currentImage.preview || "",
            category: currentCategoryOptions,
            visibleTo: currentImage.visible_to
          }}
          categories={categoryOptions}
          onCategoryChange={(_, value) => handleChangeDocumentType(value)}
          onVisibilityChange={(_, value) => {
            setVisibleTo(value);
            updateFileVisibility(currentImage.id, value);
          }}
          onCrop={handleCrop}
          cropperRef={cropperRef}
          onUpload={handleUploadClick}
          isUploading={currentImage.status === "uploading"}
          uploadProgress={currentImage.progress || 0}
          showVisibility={visibility}
          disableCategorySelection={disableCategorySelection}
          zIndex={modalZIndex}
        />
      )}
    </div>
  );
}

export default function ChunkUploadFile(props: Readonly<PureUploadFileProps>) {
  return (
    <FileUploadProvider>
      <PureUploadFile {...props} />
    </FileUploadProvider>
  );
}
